import type { NetworkCongestionEvent } from '@/recorder'
import type { SoundPlayer } from '../SoundPlayer'
import type { PayloadInjector } from '../tracker'
import { QuotaExhaustedError } from './Error'
import { RecorderProcess } from './RecorderProcess'

class RecorderEventTarget extends EventTarget {}

export interface RecorderConfig {
  quotaExhausted: boolean
  maxRecordingTimeSeconds: number
  millisecondsPerChunk: number
}

interface MutableObject<T> {
  current: T
}

export class Recorder extends RecorderEventTarget {
  #process: RecorderProcess

  readonly #soundPlayer: SoundPlayer
  readonly #config: MutableObject<RecorderConfig>
  readonly #payloadInjector: MutableObject<PayloadInjector>

  constructor(
    soundPlayer: SoundPlayer,
    payloadInjector: MutableObject<PayloadInjector>,
    config: MutableObject<RecorderConfig>
  ) {
    super()
    this.#config = config
    this.#payloadInjector = payloadInjector
    this.#soundPlayer = soundPlayer
    this.#process = this.#spawn()
  }

  get status() {
    return this.#process.status
  }

  get stream() {
    return this.#process.stream
  }

  async start() {
    if (this.#config.current.quotaExhausted) {
      throw new QuotaExhaustedError('Quota exhausted.')
    }

    this.#process = this.#spawn()
    this.#setup()
    return await this.#process.start()
  }

  resume() {
    this.#process.resume()
  }

  async stop() {
    const [finalizedResponse, blobs] = await this.#process.stop()
    this.#teardown()
    return [finalizedResponse, blobs] as const
  }

  pause() {
    return this.#process.pause()
  }

  cancel(playSound: boolean = true) {
    this.#teardown()
    return this.#process.cancel(playSound)
  }

  nextTick(cb: (duration: number) => void) {
    this.#process.nextTick(cb)
  }

  fetchMote() {
    return this.#process.fetchMote()
  }

  #setup() {
    this.#process.addEventListener('error', this.#recorderProcessErrorHandler)
    this.#process.addEventListener(
      'timelimit',
      this.#recorderProcessTimeLimitHandler
    )
    this.#process.addEventListener(
      'networkcongestion',
      this.#recorderProcessNetworkCongestionHandler
    )
  }

  #teardown() {
    this.#process.removeEventListener(
      'error',
      this.#recorderProcessErrorHandler
    )
    this.#process.removeEventListener(
      'networkcongestion',
      this.#recorderProcessNetworkCongestionHandler
    )
    this.#process.removeEventListener(
      'timelimit',
      this.#recorderProcessTimeLimitHandler
    )
  }

  #spawn() {
    return new RecorderProcess(
      this.#soundPlayer,
      this.#payloadInjector.current,
      {
        maxRecordingSeconds: this.#config.current.maxRecordingTimeSeconds,
        millisecondsPerChunk: this.#config.current.millisecondsPerChunk
      }
    )
  }

  #recorderProcessErrorHandler = {
    handleEvent: (event: ErrorEvent) => {
      this.#teardown()
      this.dispatchEvent(new ErrorEvent('error', event))
    }
  }

  #recorderProcessNetworkCongestionHandler = {
    handleEvent: (event: NetworkCongestionEvent) => {
      this.dispatchEvent(new CustomEvent('networkcongestion', event))
    }
  }

  #recorderProcessTimeLimitHandler = {
    handleEvent: (event: Event) => {
      this.dispatchEvent(new Event('timelimit', event))
    }
  }
}
